import {
  Modal,
  Text,
  Image,
  Title,
  Grid,
  Input,
  Button,
  Group,
  TextInput,
  LoadingOverlay,
  Box,
  Card,
  Stack,
  ActionIcon,
  SimpleGrid,
} from '@mantine/core';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import { useEffect, useRef, useState } from 'react';
import { useListState, useMediaQuery } from '@mantine/hooks';
import { IconPlus, IconSearch } from '@tabler/icons';
import { randomUUID } from 'crypto';
import { v4 as uuid } from 'uuid';
import { useMeasure } from '../../src/GridDnD/use-measure';
import { invoke } from '../../src/Utils';
import { EDGE_FUNCTIONS_URL } from '../../src/Constants';
import { useStore } from '../../stores/stores';

export interface ImageResult {
  name: string;
  id: string;
  thumbnail: string;
  original: string;
  source: string;
  link: string;
  width: number;
  height: number;
}

export default function ImageSearchModal({
  open,
  onClose,
  onAddImage,
  onDeleteImage,
  premiumImages,
  setPremiumImages,
}: {
  open: boolean;
  onClose: () => void;
  onDeleteImage: (id: string) => void;
  onAddImage: (img: string, name: string, id: string) => void;
  premiumImages: number;
  setPremiumImages: (premiumImages: number) => void;
}) {
  const [imageResults, setImageResults] = useState<ImageResult[]>([]);
  const supabase = useSupabaseClient();
  const session = useSession();
  const ref = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);
  const maxImagesPerPage = 12;
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [addedImages, handlers] = useListState<{ resultId: string; newId: string }>([]);
  const { setPaywallOpen, hasPremium } = useStore(null);
  const largeScreen = useMediaQuery('(min-width: 60em)');
  const searchImages = async (query: string) => {
    if (!query) {
      return;
    }
    setPage(1);
    setLoading(true);
    if (premiumImages + 1 > 4 && !hasPremium) {
      onClose();
      setPaywallOpen(true, 'maxPreview');
    }
    const token = session?.access_token;

    if (token) {
      const { data, error } = await invoke<ImageResult[]>(
        'search-image',
        EDGE_FUNCTIONS_URL,
        token,
        {
          body: {
            query,
          },
        }
      );

      setImageResults(data ?? []);
      if (data && !hasPremium) {
        setPremiumImages(premiumImages + 1);
      } else if (error && error.context.status === 402) {
        onClose();
        setPaywallOpen(true);
      }
      setLoading(false);
    }
  };

  const targetHeight = 150;
  return (
    <Modal
      opened={open}
      onClose={() => {
        handlers.filter(() => false);
        onClose();
      }}
      title={`Image Search${!hasPremium ? ` - Previews Left: ${4 - premiumImages}` : ''}`}
      size="xl"
      centered
    >
      <Text color="primary" fw={700}>
        {' '}
        Enter a prompt below to search for images. You&apos;ll be able to crop your images later.
      </Text>
      <Group w="100%">
        <TextInput
          value={value}
          onChange={(event) => setValue(event.currentTarget.value)}
          my="xl"
          placeholder="Image Name"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              if (value) {
                searchImages(value);
              }
            }
          }}
          sx={{ flex: 1 }}
        />
        <ActionIcon
          variant="filled"
          color="violet"
          onClick={() => {
            searchImages(value);
          }}
        >
          <IconSearch />
        </ActionIcon>
      </Group>
      <Text size="xs" color="dimmed">
        {' '}
        Disclaimer: Images may be subject to copyright. By uploading a photo you assert you&apos;ve
        attained the appropriate rights to the image.
      </Text>
      <Text size="xs" color="dimmed" component="a" td="underline" href="/tos" target="_blank">
        View Terms of Service.
      </Text>
      <div ref={ref}>
        <SimpleGrid
          my="xl"
          sx={{
            flex: 1,
            position: 'relative',
          }}
          mih={!largeScreen ? 100 : 758}
          cols={4}
          breakpoints={[{
            maxWidth: 600,
            cols: 2,
          }]}
        >
          <LoadingOverlay visible={loading} />
          {imageResults
            .slice(page * maxImagesPerPage - maxImagesPerPage, page * maxImagesPerPage)
            .map((result, index) => {
              const match = addedImages.find((img) => img.resultId === result.id);
              return (
                  <Card
                    shadow="sm"
                    padding="sm"
                    radius="md"
                    withBorder
                    sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                  >
                    <Card.Section sx={{ flex: 1, overflow: 'hidden', height: targetHeight }}>
                      <Image
                        radius="md"
                        src={result.thumbnail}
                        alt={result.name}
                        withPlaceholder
                        width="100%"
                        height={targetHeight}
                        fit="contain"
                        //@ts-ignore
                        sx={{
                          root: {
                            height: '100% !important',
                          },
                          [`&:hover`]: {
                            img: {
                              objectFit: 'contain !important',
                              transition: 'all 0.2s ease',
                            },
                          },
                        }}
                      />
                    </Card.Section>
                    <Stack sx={{ gap: 0, height: 80, flexShrink: 0 }}>
                      <Text
                        size="xs"
                        component="a"
                        td="underline"
                        target="_blank"
                        href={result.link}
                        title={result.source}
                        lineClamp={1}
                        my="xs"
                        sx={{
                          lineBreak: 'anywhere',
                        }}
                      >
                        {' '}
                        Source: {result.source}
                      </Text>
                      <Button
                        variant="light"
                        color={match ? 'red' : 'green'}
                        fullWidth
                        radius="md"
                        onClick={() => {
                          if (match) {
                            onDeleteImage(match.newId);
                            handlers.filter((itm) => itm.resultId !== result.id);
                          } else {
                            const newId = uuid();
                            handlers.append({ resultId: result.id, newId });
                            onAddImage(result.thumbnail, value, newId);
                          }
                        }}
                      >
                        {match ? 'Remove' : 'Add'}
                      </Button>
                    </Stack>
                  </Card>
              );
            })}
        </SimpleGrid>
        {imageResults.length > 0 && (
          <Group w="100%" position="apart">
            <Button
              variant="outline"
              disabled={page === 1}
              onClick={() => {
                setPage(page - 1);
              }}
            >
              {' '}
              Previous
            </Button>
            <Button
              disabled={page >= 48 / maxImagesPerPage}
              onClick={() => {
                setPage(page + 1);
              }}
            >
              {' '}
              Next{' '}
            </Button>
          </Group>
        )}
      </div>
    </Modal>
  );
}
